import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import SelectField from './MultiSelectChips';
import CalendarRangeInput from './CalendarRangeInput';
import { formatFulfillmentMethod } from '../utils/helper';

// Export drawer for the platform order tables (ERP-5041).
//
// Separate from FilterDrawer because it confirms with a single Export action and carries
// the default-behaviour note under it, rather than the Clear/Cancel/Apply footer. The
// chip fields are the shared MultiSelectChips (moved out of this file on 9 Oct for
// ERP-5249), which mirrors FilterDrawer's "simpleList" markup — same chip container,
// yellow chips, rotating chevron, checked-row tick — so the drawers read as one control set.

const LABEL = {
    display: 'block',
    fontFamily: 'Montserrat',
    fontSize: 12,
    fontWeight: 600,
    color: '#212529',
    marginBottom: 6,
};

/**
 * Multi-select matching FilterDrawer's simpleList. Pass `searchable` for the
 * "Search and Select..." variant, which adds a type-to-filter input inline with the chips.
 */
export default function ExportOrdersDrawer({
    isOpen,
    onClose,
    values,
    setValues,
    statusOptions = [],
    branchOptions = [],
    fulfillmentOptions = [],
    onExport,
    isExporting = false,
}) {
    const [openField, setOpenField] = useState(null);

    useEffect(() => { if (!isOpen) setOpenField(null); }, [isOpen]);

    if (!isOpen) return null;

    const set = (key, v) => setValues(prev => ({ ...prev, [key]: v }));

    // Empties every field back to the drawer's initial state — the same {} the page
    // starts exportValues at, so a cleared drawer exports the documented default
    // (all statuses, today). CalendarRangeInput mirrors its props, so blanking
    // order_time_start/end clears the visible range too.
    const handleClear = () => {
        setValues({});
        setOpenField(null);
    };
    const fieldProps = (key) => ({
        isOpen: openField === key,
        onToggle: () => setOpenField(prev => (prev === key ? null : key)),
        onClose: () => setOpenField(prev => (prev === key ? null : prev)),
    });

    return (
        <>
            <div className="drawer-overlay" onClick={onClose} />
            <div className="entity-drawer" style={{ width: 400 }}>
                <div className="drawer-header" style={{ backgroundColor: '#F8F9FA', padding: '15px 20px' }}>
                    <h3 style={{ margin: 0 }}>Export orders</h3>
                    <button className="close-btn" onClick={onClose}><X size={18} /></button>
                </div>

                <div
                    className="main-drawer-inner"
                    style={{ padding: '15px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                    <div style={{ marginBottom: 14 }}>
                        <label style={LABEL}>Order Time</label>
                        <CalendarRangeInput
                            name="exportOrderTime"
                            enableTime
                            startDate={values.order_time_start || null}
                            endDate={values.order_time_end || null}
                            onChange={(e) => setValues(prev => ({
                                ...prev,
                                order_time_start: e.target.value.start,
                                order_time_end: e.target.value.end,
                            }))}
                        />
                    </div>

                    <div style={{ marginBottom: 14 }}>
                        <label style={LABEL}>Order Status</label>
                        <SelectField
                            placeholder="Select..."
                            options={statusOptions}
                            selected={values.order_status || []}
                            onChange={(v) => set('order_status', v)}
                            {...fieldProps('status')}
                        />
                    </div>

                    <div style={{ marginBottom: 14 }}>
                        <label style={LABEL}>Linked Branch</label>
                        <SelectField
                            searchable
                            placeholder="Search and Select..."
                            options={branchOptions}
                            selected={values.linked_branch || []}
                            onChange={(v) => set('linked_branch', v)}
                            {...fieldProps('branch')}
                        />
                    </div>

                    <div style={{ marginBottom: 18 }}>
                        <label style={LABEL}>Fulfillment Method</label>
                        <SelectField
                            placeholder="Select..."
                            options={fulfillmentOptions}
                            selected={values.fulfillment_method || []}
                            onChange={(v) => set('fulfillment_method', v)}
                            renderLabel={formatFulfillmentMethod}
                            {...fieldProps('fulfillment')}
                        />
                    </div>

                    <button
                        type="button"
                        onClick={onExport}
                        disabled={isExporting}
                        style={{
                            width: '100%',
                            height: 40,
                            borderRadius: 8,
                            border: 'none',
                            background: '#212529',
                            color: '#fff',
                            fontFamily: 'Montserrat',
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: isExporting ? 'not-allowed' : 'pointer',
                            opacity: isExporting ? 0.6 : 1,
                        }}
                    >
                        {isExporting ? 'Exporting…' : 'Export'}
                    </button>

                    <p style={{
                        margin: '10px 0 0',
                        fontFamily: 'Montserrat',
                        fontSize: 12,
                        lineHeight: 1.5,
                        color: '#6C757D',
                    }}>
                        If you don't set any filters, orders in all statuses from today will be
                        exported by default.
                    </p>

                    {/* Clear resets the fields in place; Close dismisses without exporting.
                        The row is positioned to land Clear and Close exactly where FilterDrawer
                        puts its Clear and Filter buttons, so the two drawers' footers line up
                        when you switch between them:
                          - space-between + the parent's 20px side padding puts Clear's left edge
                            and Close's right edge 20px off each panel edge, matching FilterDrawer's
                            footer (a direct child of the 400px shell with padding "40px 20px").
                          - paddingBottom 25 + main-drawer-inner's 15px = 40px under the row, the
                            same clearance FilterDrawer's 40px bottom padding leaves.
                          - marginTop:auto pins the row to the bottom of the panel instead of
                            trailing straight after the note; FilterDrawer's footer gets there by
                            following a flex:1 body. */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 50,
                        marginTop: 'auto',
                        padding: '40px 0 25px',
                    }}>
                        <button
                            type="button"
                            onClick={handleClear}
                            style={{
                                background: 'none',
                                border: 'none',
                                // FilterDrawer's Clear declares width:140 but renders at 102 —
                                // its Cancel+Filter group (100 + 8 + 100, border-box) plus the
                                // 50px gap leaves only 102 of the 360px content box, and the group
                                // can't shrink past its min-content. Matching the rendered width
                                // keeps the centred label on the same x as that drawer's.
                                width: 102,
                                padding: 0,
                                color: '#5A6A7D',
                                fontFamily: 'Montserrat',
                                fontSize: 12,
                                fontWeight: 600,
                                lineHeight: 'normal',
                                cursor: 'pointer',
                            }}
                        >
                            Clear
                        </button>

                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                display: 'flex',
                                height: 34,
                                minWidth: 100,
                                padding: 10,
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 10,
                                borderRadius: 8,
                                background: 'var(--Background-White, #FFF)',
                                border: '1px solid var(--navbar-dropdown-active, #212529)',
                                color: 'var(--navbar-dropdown-active, #212529)',
                                boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                fontFamily: 'Montserrat',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
